import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Register the TextPlugin for the typewriter effect
gsap.registerPlugin(TextPlugin);

const { FiArrowRight } = FiIcons;

export default function HeroSection() {
  const headlineRef = useRef(null);
  const accentRef = useRef(null);
  const subtitleRef = useRef(null);
  const badgeRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'none' } });

    // Initial state: hide badge and CTAs to start
    gsap.set([badgeRef.current, ctaRef.current], { opacity: 0, y: 20 });
    
    // Store original text
    const headlineText = "Building human potential at the intersection of";
    const accentText = "AI, leadership, creativity, and faith.";
    const subtitleText = "Dr. Jeff Robinson helps individuals and organizations embrace innovation with clarity, confidence, and purpose — at the intersection of technology and humanity.";

    // Clear text for typing animation
    if (headlineRef.current) headlineRef.current.textContent = "";
    if (accentRef.current) accentRef.current.textContent = "";
    if (subtitleRef.current) subtitleRef.current.textContent = "";

    // Typewriter sequence
    tl.to(badgeRef.current, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" })
      .to(headlineRef.current, { text: headlineText, duration: 1.5 })
      .to(accentRef.current, { text: accentText, duration: 1.2 })
      .to(subtitleRef.current, { text: subtitleText, duration: 2.2 })
      .to(ctaRef.current, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, "+=0.2");

    return () => tl.kill();
  }, []);

  return (
    <div className="w-full lg:w-[60%] xl:w-[55%] ml-auto text-left py-20 lg:py-32">
      {/* Badge */}
      <div ref={badgeRef} className="inline-flex items-center rounded-full border border-blue-300/20 bg-[#060b19]/60 backdrop-blur-md px-4 py-1.5 text-xs uppercase tracking-[0.26em] text-blue-100/80 mb-6">
        Public Figure • Leadership Architect • Technologist
      </div>

      {/* Headline */}
      <h1 ref={headlineRef} className="min-h-[1.2em] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight text-white leading-tight mb-2">
      </h1>

      {/* Gradient Accent */}
      <p ref={accentRef} className="min-h-[1.2em] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight bg-gradient-to-r from-blue-300 via-purple-300 to-orange-300 bg-clip-text text-transparent leading-tight mb-8">
      </p>

      {/* Subtitle */}
      <p ref={subtitleRef} className="min-h-[1.2em] text-base sm:text-lg lg:text-xl leading-8 text-white/80 mb-12">
      </p>

      {/* CTA Group */}
      <div ref={ctaRef} className="flex flex-col sm:flex-row items-start justify-start gap-4 flex-wrap">
        <a
          href="https://sendfox.com/jrobunited"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500 px-7 py-3.5 text-sm font-medium text-white shadow-[0_10px_40px_rgba(99,102,241,0.35)] transition hover:scale-[1.03]"
        >
          Get Insider Access <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
        </a>
        <a
          href="#brands"
          className="inline-flex items-center justify-center rounded-full border border-white/15 bg-[#060b19]/60 backdrop-blur-md px-7 py-3.5 text-sm font-medium text-white/90 transition hover:bg-white/10"
        >
          Explore the Brands
        </a>
        <a
          href="https://drjeff.jrobunited.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full border border-white/15 bg-[#060b19]/60 backdrop-blur-md px-7 py-3.5 text-sm font-medium text-white/90 transition hover:bg-white/10"
        >
          See Me In Action
        </a>
      </div>
    </div>
  );
}